import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Send, Plus, Sparkles, Loader2, Gauge, Wand2, Rocket, Users2,
  Mail, Linkedin, ArrowLeft, ShieldAlert, Video, Copy, Trash2, LayoutTemplate,
} from "lucide-react";
import { PageBody, PageHeader, EmptyState } from "@/components/Page";
import { ManualSendBanner } from "@/components/ManualSendBanner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FieldHelp, LabelHelp, PageTip } from "@/components/FieldHelp";
import { HELP } from "@/lib/fieldHelp";
import api from "@/lib/api";

const isLoom = (u) => { try { const h = new URL(u).hostname.toLowerCase(); return h === "loom.com" || h === "www.loom.com"; } catch { return false; } };

function Metric({ label, value, sub, note }) {
  return (
    <div className="rounded-md bg-accent/40 py-2 text-center" title={note || ""}>
      <div className="metric text-base">{value}{sub != null ? <span className="text-[10px] text-muted-foreground ml-1">{sub}</span> : null}</div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function SpamMeter({ score }) {  const color = score <= 25 ? "text-emerald-500" : score <= 50 ? "text-amber-500" : "text-rose-500";
  const bg = score <= 25 ? "bg-emerald-500" : score <= 50 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="flex items-center gap-2" data-testid="spam-score-indicator">
      <Gauge className={`h-4 w-4 ${color}`} />
      <div className="w-24 h-1.5 rounded-full bg-accent overflow-hidden"><div className={`h-full ${bg}`} style={{ width: `${score}%` }} /></div>
      <span className={`text-xs font-mono ${color}`}>{score}</span>
    </div>
  );
}

export function SequenceEditor({ campaign, onBack, reload }) {
  const [steps, setSteps] = useState(campaign.steps || []);
  const [busy, setBusy] = useState(null);
  const [scores, setScores] = useState({});
  const [rwOpen, setRwOpen] = useState(null);
  const [rwNote, setRwNote] = useState("");
  const [contacts, setContacts] = useState([]);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [autoSend, setAutoSend] = useState(campaign.auto_send);
  const [approvedAt, setApprovedAt] = useState(campaign.template_approved_at || null);
  const [sampleReview, setSampleReview] = useState(true);
  const [usage, setUsage] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tplOpen, setTplOpen] = useState(false);
  const [tpl, setTpl] = useState({ name: "", description: "" });
  const saveTemplate = async () => {
    if (!tpl.name.trim()) return toast.error("Name the template");
    await saveSteps();
    try { await api.post(`/campaigns/${campaign.id}/save-as-template`, tpl); toast.success("Saved as workspace template"); setTplOpen(false); setTpl({ name: "", description: "" }); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not save template", { duration: 7000 }); }
  };
  const wsId = user?.active_workspace_id;
  const canManage = ["owner", "admin"].includes(user?.role);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (wsId) api.get(`/workspaces/${wsId}/sample-review`).then((r) => setSampleReview(r.data.enabled)).catch(() => {}); }, [wsId]);
  const loadUsage = () => api.get("/enroll-usage").then((r) => setUsage(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (enrollOpen) loadUsage(); }, [enrollOpen]);
  useEffect(() => {
    if (searchParams.get("enroll") === "1") {
      if (approvedAt) setEnrollOpen(true);
      else toast.info("Approve the template, then enroll your first 5 samples");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const approveTemplate = async () => {
    await saveSteps();
    try { const { data } = await api.post(`/campaigns/${campaign.id}/approve-template`); setApprovedAt(data.template_approved_at); toast.success("Sequence template approved — you can now enroll"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Only owners and admins can approve the template"); }
  };
  const toggleSampleReview = async (v) => {
    setSampleReview(v);
    try { await api.put(`/workspaces/${wsId}/sample-review`, { enabled: v }); toast.success(v ? "Review 5 samples, send the rest" : "Auto-send all first-touches"); }
    catch (e) { setSampleReview(!v); toast.error(e?.response?.data?.detail?.message || "Only owners and admins can change this"); }
  };

  const updateStep = (i, patch) => setSteps(steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const saveSteps = async (next) => {
    const payload = next || steps;
    await api.put(`/campaigns/${campaign.id}/steps`, { steps: payload });
  };

  const saveLoom = async (i) => {
    const lu = steps[i].loom_url || "";
    if (lu && !isLoom(lu)) return toast.error("Loom link must be a loom.com share URL");
    await saveSteps();
  };

  const rewrite = async (i, note) => {
    setBusy(`rw-${i}`);
    try {
      const { data } = await api.post("/ai/rewrite", { body: steps[i].body, subject: steps[i].subject || "", campaign_id: campaign.id, note: note || "" });
      const next = steps.map((s, idx) => (idx === i ? { ...s, body: data.body, subject: data.subject || s.subject } : s));
      setSteps(next); await saveSteps(next);
      toast.success("Step rewritten by Copilot");
      setRwOpen(null); setRwNote("");
    } catch { toast.error("Rewrite failed"); } finally { setBusy(null); }
  };

  const score = async (i) => {
    setBusy(`sc-${i}`);
    try {
      const { data } = await api.post("/ai/score", { subject: steps[i].subject || "", body: steps[i].body });
      setScores({ ...scores, [i]: data });
    } catch { toast.error("Scoring failed"); } finally { setBusy(null); }
  };

  const addStep = () => setSteps([...steps, { day: (steps[steps.length - 1]?.day || 0) + 3, channel: "email", purpose: "Follow-up", subject: "", body: "" }]);

  const enroll = async (limit) => {
    const ids = limit ? selectedContacts.slice(0, limit) : selectedContacts;
    if (!ids.length) return toast.error("Select contacts");
    await saveSteps();
    try {
      const { data } = await api.post(`/campaigns/${campaign.id}/enroll`, { contact_ids: ids });
      const parts = [`Enrolled ${data.enrolled}`];
      if (data.samples) parts.push(`${data.samples} queued for review`);
      if (data.auto_sending) parts.push(`${data.auto_sending} auto-sending`);
      if (data.capped) parts.push(`${data.capped} over plan cap`);
      if (data.skipped_no_email) parts.push(`${data.skipped_no_email} no email (LinkedIn queue)`);
      toast.success(parts.join(" · "));
      setEnrollOpen(false); setSelectedContacts([]); reload();
    } catch (e) {
      if (e?.response?.data?.detail?.code === "template_not_approved") toast.error("Approve the sequence template first");
      else if (e?.response?.data?.detail?.code === "enroll_cap") toast.error(e.response.data.detail.message);
      else toast.error(e?.response?.data?.detail?.message || "Enroll failed");
    } finally { loadUsage(); }
  };

  const launch = async () => {
    await saveSteps();
    await api.post(`/campaigns/${campaign.id}/launch`, { auto_send: autoSend });
    toast.success(autoSend ? "Campaign launched (auto-send on)" : "Campaign launched - first-touch approval required");
    reload();
  };

  const overrideType = async (t) => {
    await api.put(`/campaigns/${campaign.id}/type`, { type: t });
    toast.success(`Sector set to ${t === "public_sector" ? "public" : "private"} (override)`);
    reload();
  };

  return (
    <div>
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-4" data-testid="back-to-campaigns"><ArrowLeft className="h-4 w-4" /> All campaigns</button>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-2xl font-semibold tracking-tight">{campaign.name}</h1>
            <Badge className={campaign.type === "public_sector" ? "bg-sky-500/15 text-sky-500 border-0" : "bg-amber-500/15 text-amber-500 border-0"} data-testid="campaign-sector-badge">
              {campaign.recommended_sector === "both" ? "Mixed" : campaign.type === "public_sector" ? "Mostly public" : "Mostly private"} · AI pick
            </Badge>
            <Select value={campaign.type} onValueChange={overrideType}>
              <SelectTrigger className="h-7 w-[148px] text-xs" data-testid="campaign-type-override"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="private">Private sector</SelectItem><SelectItem value="public_sector">Public sector</SelectItem></SelectContent>
            </Select>
            <Badge variant="outline" className="capitalize">{campaign.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{campaign.angle}</p>
        </div>
        <div className="flex items-center gap-2">
          {campaign.offer_id && <Button variant="ghost" className="rounded-md" onClick={() => navigate("/app/leads")} data-testid="find-leads-btn"><Users2 className="h-4 w-4 mr-1.5" /> Find leads for this offer</Button>}
          {approvedAt
            ? <Badge className="bg-emerald-500/15 text-emerald-500 border-0" data-testid="template-approved-badge"><ShieldAlert className="h-3 w-3 mr-1" /> Template approved</Badge>
            : canManage
              ? <Button variant="outline" className="rounded-md" onClick={approveTemplate} data-testid="approve-template-btn"><ShieldAlert className="h-4 w-4 mr-1.5" /> Approve template</Button>
              : <Badge variant="outline" className="text-amber-500 border-amber-500/40" data-testid="template-pending-badge">Awaiting admin approval</Badge>}
          <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-md" disabled={!approvedAt} data-testid="enroll-btn"><Users2 className="h-4 w-4 mr-1.5" /> Enroll</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Enroll contacts</DialogTitle><DialogDescription>First-touch uses your approved template with merge fields. Replies, unsubscribes, bounces and disconnected mailboxes stop sends automatically.</DialogDescription></DialogHeader>
              {canManage && (
                <div className="flex items-center justify-between rounded-lg border border-border/60 bg-card/40 p-3">
                  <div className="pr-3">
                    <div className="text-sm font-medium">Review 5 samples, send the rest</div>
                    <div className="text-xs text-muted-foreground">{sampleReview ? "The first 5 first-touches go to your Approval Queue; the rest auto-send from the approved template." : "All first-touches auto-send from the approved template."}</div>
                  </div>
                  <Switch checked={sampleReview} onCheckedChange={toggleSampleReview} data-testid="sample-review-toggle" />
                </div>
              )}
              {usage && (
                <div className="text-xs text-muted-foreground px-0.5" data-testid="enroll-usage-label">
                  {usage.unlimited
                    ? "Unlimited contacts on your plan."
                    : <>{usage.used} of {usage.cap} contacts enrolled this period.{usage.remaining <= 0 && <> <a href="/app/admin/billing" className="text-amber-500 underline" data-testid="enroll-upgrade-link">Upgrade to enroll more</a>.</>}</>}
                </div>
              )}
              {usage && !usage.unlimited && usage.remaining > 0 && selectedContacts.length > usage.remaining && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs" data-testid="enroll-cap-warning">
                  You selected {selectedContacts.length} but your plan has {usage.remaining} left this period. We'll enroll the first {usage.remaining} — the rest stay unselected. <a href="/app/admin/billing" className="text-amber-500 underline" data-testid="enroll-cap-upgrade">Upgrade</a> to enroll more.
                </div>
              )}
              <div className="max-h-72 overflow-y-auto space-y-1.5">
                {contacts.map((c) => {
                  const noEmail = !c.email;
                  return (
                  <label key={c.id} className={`flex items-center gap-3 p-2.5 rounded-md border border-border/60 ${noEmail ? "opacity-60 cursor-not-allowed" : "hover:bg-accent/40 cursor-pointer"}`}>
                    <input type="checkbox" disabled={noEmail} checked={selectedContacts.includes(c.id)}
                      onChange={(e) => setSelectedContacts(e.target.checked ? [...selectedContacts, c.id] : selectedContacts.filter((x) => x !== c.id))}
                      className="accent-amber-500" data-testid={`enroll-contact-${c.id}`} />
                    <span className="flex-1"><span className="text-sm font-medium">{c.name}</span><span className="block text-xs text-muted-foreground">{c.title} · {c.company}{noEmail ? " · no email - LinkedIn queue only" : ""}</span></span>
                    <span className="metric text-xs text-muted-foreground">{c.fit_score}</span>
                  </label>
                  );
                })}
              </div>
              <DialogFooter>
                {usage && !usage.unlimited && usage.remaining > 0 && selectedContacts.length > usage.remaining
                  ? <Button onClick={() => enroll(usage.remaining)} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="confirm-enroll-btn">Enroll first {usage.remaining}</Button>
                  : <Button onClick={() => enroll()} disabled={usage && !usage.unlimited && usage.remaining <= 0} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="confirm-enroll-btn">Enroll {selectedContacts.length || ""}</Button>}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={launch} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="launch-btn"><Rocket className="h-4 w-4 mr-1.5" /> Launch</Button>
          <Dialog open={tplOpen} onOpenChange={setTplOpen}>
            <DialogTrigger asChild><Button variant="ghost" className="rounded-md" data-testid="save-template-btn">Save as workspace template</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Save as workspace template</DialogTitle><DialogDescription>Reuse this sequence when creating a new brief. Templates are workspace-scoped. If the source offer had no proof, the template can't contain numbers, awards, or growth claims.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Name</Label><Input value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} className="mt-1.5" data-testid="template-name" /></div>
                <div><Label className="text-xs">Description</Label><Textarea value={tpl.description} onChange={(e) => setTpl({ ...tpl, description: e.target.value })} rows={2} className="mt-1.5" data-testid="template-description" /></div>
              </div>
              <DialogFooter><Button onClick={saveTemplate} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="template-save-confirm">Save template</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-lg border border-border/60 bg-card/40 p-4 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-500" />
          <div>
            <div className="text-sm font-medium">First-touch approval {autoSend ? "off" : "on"}</div>
            <div className="text-xs text-muted-foreground">{autoSend ? "Emails auto-send once launched." : "Every first-touch email is queued for your review before sending."}</div>
          </div>
        </div>
        <Switch checked={!autoSend} onCheckedChange={(v) => setAutoSend(!v)} data-testid="approval-toggle" />
      </div>

      {campaign.analytics && (
        <div className="mb-6" data-testid="sequence-analytics">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric label="Enrolled" value={campaign.analytics.enrolled} />
            <Metric label="Sent" value={campaign.analytics.sent} />
            <Metric label="Replied" value={campaign.analytics.replied} sub={`${campaign.analytics.reply_rate}%`} />
            <Metric label="Meetings" value={campaign.analytics.meetings} sub={`${campaign.analytics.meeting_rate}%`} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Open tracking isn't enabled (no tracking pixel) - reply and meeting rates are measured against sends.</p>

          {(campaign.analytics.by_step?.length > 0 || Object.keys(campaign.analytics.by_source || {}).length > 0) && (
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="rounded-xl border border-border/60 bg-card/40 p-4" data-testid="analytics-by-step">
                <div className="eyebrow mb-3">By step</div>
                <div className="space-y-1.5">
                  {(campaign.analytics.by_step || []).map((s) => (
                    <div key={s.index} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="h-5 w-5 rounded bg-amber-500/15 text-amber-500 text-[10px] font-mono flex items-center justify-center shrink-0">D{s.day}</span>
                        {s.channel === "email" ? <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <Linkedin className="h-3.5 w-3.5 text-sky-500 shrink-0" />}
                        <span className="truncate text-muted-foreground">{s.purpose || `Step ${s.index + 1}`}</span>
                      </span>
                      <span className="metric text-xs shrink-0">{s.sent} sent</span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">First-touch is the only auto-send; later steps send after your review.</p>
              </div>
              <div className="rounded-xl border border-border/60 bg-card/40 p-4" data-testid="analytics-by-source">
                <div className="eyebrow mb-3">By source</div>
                <div className="space-y-1.5">
                  {Object.entries(campaign.analytics.by_source || {}).length === 0
                    ? <p className="text-xs text-muted-foreground">No enrolled contacts yet.</p>
                    : Object.entries(campaign.analytics.by_source).map(([src, n]) => (
                      <div key={src} className="flex items-center justify-between text-sm">
                        <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-accent/60 text-muted-foreground">{src.replace(/_/g, " ")}</span>
                        <span className="metric text-xs">{n}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-1.5 mb-3">
        <span className="eyebrow">Sequence steps</span>
        <FieldHelp title={campaign.type === "public_sector" ? "Public-sector sequence" : "Sequence steps"} testid="help-seq-steps" {...(campaign.type === "public_sector" ? HELP.seq_public : HELP.seq_steps)} />
      </div>
      <div className="space-y-4">
        {steps.map((step, i) => (
          <div key={`${step.day}-${step.channel}-${i}`} className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid={`sequence-step-card`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <div className="flex flex-wrap items-center gap-2.5 min-w-0">
                <span className="h-7 w-7 rounded-md bg-amber-500/15 text-amber-500 text-xs font-mono flex items-center justify-center">D{step.day}</span>
                {step.channel === "email" ? <Mail className="h-4 w-4 text-muted-foreground" /> : <Linkedin className="h-4 w-4 text-sky-500" />}
                <span className="text-sm font-medium">{step.purpose}</span>
                {i === 0
                  ? <Badge className="bg-amber-500/15 text-amber-500 border-0 text-[10px]" data-testid="first-touch-badge">First-touch - Approval Queue</Badge>
                  : <FieldHelp title="Personalize" testid={`help-seq-personalize-${i}`} {...HELP.seq_personalize} />}
              </div>
              <div className="flex items-center gap-2">
                {scores[i] && <SpamMeter score={scores[i].spam_risk} />}
                <Button size="sm" variant="ghost" onClick={() => score(i)} disabled={busy === `sc-${i}`} data-testid={`score-btn-${i}`}>{busy === `sc-${i}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Gauge className="h-3.5 w-3.5" />}</Button>
                <Button size="sm" variant="ghost" onClick={() => { setRwOpen(rwOpen === i ? null : i); setRwNote(""); }} disabled={busy === `rw-${i}`} data-testid={`ai-rewrite-button-${i}`} className="text-amber-500">{busy === `rw-${i}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} <span className="ml-1 text-xs">AI rewrite</span></Button>
              </div>
            </div>
            {rwOpen === i && (
              <div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`rewrite-note-row-${i}`}>
                <Input value={rwNote} onChange={(e) => setRwNote(e.target.value)} placeholder="shorter / more urgent / no jargon" className="text-sm h-9 flex-1" data-testid={`rewrite-note-input-${i}`} onKeyDown={(e) => { if (e.key === "Enter") rewrite(i, rwNote); }} />
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => rewrite(i, rwNote)} disabled={busy === `rw-${i}`} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid={`rewrite-note-submit-${i}`}>{busy === `rw-${i}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Rewrite"}</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setRwOpen(null); setRwNote(""); }} data-testid={`rewrite-note-cancel-${i}`}>Cancel</Button>
                </div>
              </div>
            )}
            {step.channel === "email" && (
              <Input value={step.subject || ""} onChange={(e) => updateStep(i, { subject: e.target.value })} onBlur={() => saveSteps()} placeholder="Subject" className="mb-2 font-medium" data-testid={`step-subject-${i}`} />
            )}
            <Textarea value={step.body} onChange={(e) => updateStep(i, { body: e.target.value })} onBlur={() => saveSteps()} rows={5} className="text-sm" data-testid={`step-body-${i}`} />
            {step.channel === "email" && (
              <div className="mt-3" data-testid={`step-loom-wrap-${i}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Video className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Loom walkthrough (optional)</span>
                </div>
                <Input value={step.loom_url || ""} onChange={(e) => updateStep(i, { loom_url: e.target.value })} onBlur={() => saveLoom(i)}
                  placeholder="https://www.loom.com/share/…" data-testid={`step-loom-${i}`}
                  className={`text-sm ${step.loom_url && !isLoom(step.loom_url) ? "border-rose-500" : ""}`} />
                {step.loom_url && !isLoom(step.loom_url) && <p className="text-[11px] text-rose-500 mt-1">Must be a loom.com share link.</p>}
                <p className="text-[11px] text-muted-foreground mt-1">15 to 30 seconds. Say their name, one problem, one ask. Record in your Loom account. Same clip is fine for one ICP, not for a whole list of different roles. Added after the email body as "20-second walkthrough:" — no player is embedded in the sent email.</p>
              </div>
            )}
            {scores[i]?.notes?.length > 0 && (
              <ul className="mt-2 text-xs text-muted-foreground list-disc pl-4 space-y-0.5">{scores[i].notes.slice(0, 3).map((n, k) => <li key={`${i}-${k}`}>{n}</li>)}</ul>
            )}
          </div>
        ))}
        <Button variant="outline" onClick={addStep} className="rounded-md w-full border-dashed" data-testid="add-step-btn"><Plus className="h-4 w-4 mr-1.5" /> Add step</Button>
      </div>
    </div>
  );
}

function TemplatesPanel({ canManage }) {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [openId, setOpenId] = useState(null);
  const load = () => api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const rename = async (t) => {
    if (!name.trim()) return toast.error("Name can't be empty");
    try { await api.patch(`/templates/${t.id}`, { name }); toast.success("Template renamed"); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Rename failed"); }
  };
  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This can't be undone.`)) return;
    try { await api.delete(`/templates/${t.id}`); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Delete failed"); }
  };
  if (!templates.length) return <EmptyState icon={LayoutTemplate} title="No templates yet" body='Open a saved campaign and use "Save as workspace template" to reuse its sequence here.' />;
  return (
    <div className="space-y-3" data-testid="templates-panel">
      {templates.map((t) => (
        <div key={t.id} className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid={`template-row-${t.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {editing === t.id ? (
                <div className="flex items-center gap-2">
                  <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 max-w-xs" data-testid={`template-rename-input-${t.id}`} onKeyDown={(e) => { if (e.key === "Enter") rename(t); }} />
                  <Button size="sm" onClick={() => rename(t)} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid={`template-rename-save-${t.id}`}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-semibold tracking-tight">{t.name}</div>
                    <Badge variant="outline" className="text-xs">{t.type === "public_sector" ? "public" : "private"}</Badge>
                    <Badge variant="outline" className="text-xs capitalize">{t.tone || "peer"}</Badge>
                    <span className="text-xs text-muted-foreground">{(t.steps || []).length} steps</span>
                  </div>
                  {t.description && <p className="text-xs text-muted-foreground mt-1">{t.description}</p>}
                </>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button size="sm" variant="ghost" onClick={() => setOpenId(openId === t.id ? null : t.id)} data-testid={`template-preview-btn-${t.id}`}>{openId === t.id ? "Hide" : "Preview"}</Button>
              {editing !== t.id && <Button size="sm" variant="outline" className="rounded-md" onClick={() => { setEditing(t.id); setName(t.name); }} data-testid={`template-rename-btn-${t.id}`}>Rename</Button>}
              {canManage && <Button size="sm" variant="ghost" className="text-rose-500 hover:text-rose-400" onClick={() => del(t)} data-testid={`template-delete-btn-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
            </div>
          </div>
          {openId === t.id && (
            <div className="mt-4 space-y-2.5 border-t border-border/40 pt-3" data-testid={`template-steps-${t.id}`}>
              {(t.steps || []).map((s, i) => (
                <div key={i} className="text-sm">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="h-5 w-5 rounded bg-amber-500/15 text-amber-500 text-[10px] font-mono flex items-center justify-center shrink-0">D{s.day}</span>
                    {(s.channel || "email") === "email" ? <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <Linkedin className="h-3.5 w-3.5 text-sky-500 shrink-0" />}
                    <span className="font-medium">{s.purpose || `Step ${i + 1}`}</span>
                    {s.subject && <span className="text-xs text-muted-foreground truncate">· {s.subject}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground whitespace-pre-line line-clamp-3 pl-7">{s.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [gen, setGen] = useState(false);
  const [form, setForm] = useState({ name: "", offer_id: "", angle: "", strategy_mode: "high-volume outbound" });
  const [tab, setTab] = useState("campaigns");
  const [dup, setDup] = useState(null);
  const [dupOffer, setDupOffer] = useState("");
  const { user } = useAuth();
  const canManage = ["owner", "admin"].includes(user?.role);

  const load = async () => {
    const { data } = await api.get("/campaigns");
    setCampaigns(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); api.get("/offers").then((r) => { setOffers(r.data); }); }, []);

  const generateAndCreate = async () => {
    if (!form.name) return toast.error("Name your campaign (motion + quarter).");
    if (!form.offer_id) return toast.error("Create an offer first. The sequence is written from the offer, not the campaign name.");
    setGen(true);
    try {
      const seq = await api.post("/campaigns/generate-sequence", { offer_id: form.offer_id, strategy_mode: form.strategy_mode });
      const rsec = seq.data.recommended_sector || "private";
      const { data } = await api.post("/campaigns", { name: form.name, offer_id: form.offer_id, strategy_mode: form.strategy_mode, type: seq.data.campaign_type || "private", recommended_sector: rsec, steps: seq.data.steps || [], angle: form.angle || seq.data.summary });
      setOpen(false); setForm({ name: "", offer_id: "", angle: "", strategy_mode: "high-volume outbound" });
      await load();
      toast.success(`Campaign created - DealsOps targeted ${rsec === "both" ? "private + public" : rsec} buyers. Review the list and first email.`);
      navigate(`/app/campaigns/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Generation failed"); } finally { setGen(false); }
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/campaigns/${dup.id}/duplicate`, { offer_id: dupOffer || null });
      toast.success("Duplicated — first-touch needs approval again");
      setDup(null); navigate(`/app/campaigns/${data.campaign_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not duplicate"); }
  };

  return (
    <PageBody>
      <PageHeader eyebrow="Campaigns & Sequences" title="Outbound, drafted and gated by AI"
        subtitle="Generate a 4-7 step sequence from any offer, tune the copy, enroll contacts, and launch with first-touch approval."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="new-campaign-btn"><Plus className="h-4 w-4 mr-1.5" /> New campaign</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New AI campaign</DialogTitle><DialogDescription>Describe the offer. DealsOps chooses who to contact - private or public - and builds the list. You approve the list and the first email.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div><LabelHelp label="Campaign name" help={HELP.campaign_name} testid="help-campaign-name" /><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="campaign-name" placeholder="RevOps Outbound Q3" /></div>
                <div><LabelHelp label="Offer" help={HELP.campaign_offer} testid="help-campaign-offer" />
                  {offers.length === 0 ? (
                    <div className="mt-1.5 rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 p-4 text-center" data-testid="no-offers-msg">
                      <p className="text-sm text-foreground">No offers in this workspace yet.</p>
                      <p className="text-xs text-muted-foreground mt-1">Create an offer first. The sequence is written from the offer, not the campaign name.</p>
                      <Button size="sm" variant="outline" className="rounded-md mt-3" data-testid="create-offer-cta"
                        onClick={() => { setOpen(false); navigate("/app/offer"); }}>
                        Create an offer
                      </Button>
                    </div>
                  ) : (
                    <Select value={form.offer_id} onValueChange={(v) => setForm({ ...form, offer_id: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="campaign-offer"><SelectValue placeholder="Select an offer" /></SelectTrigger>
                      <SelectContent>{offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.product}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div><LabelHelp label="Strategy" help={HELP.campaign_typestrat} testid="help-campaign-strategy" />
                    <Select value={form.strategy_mode} onValueChange={(v) => setForm({ ...form, strategy_mode: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high-volume outbound">High-volume outbound</SelectItem>
                        <SelectItem value="account-based">Account-based</SelectItem>
                        <SelectItem value="public-sector procurement">Public-sector procurement</SelectItem>
                        <SelectItem value="product-led follow-up">Product-led follow-up</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="ai-picks-note">DealsOps reads your offer and ICP, then chooses who to contact (private or public) and a source mix. You approve the list and the first email.</p>
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={generateAndCreate} disabled={gen || offers.length === 0} className="rounded-md bg-amber-500 text-black hover:bg-amber-400 min-h-[44px]" data-testid="generate-campaign-btn">{gen ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate sequence</>}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <ManualSendBanner />

      <PageTip id="campaigns">
        Describe the offer and pick it here. <span className="text-foreground font-medium">DealsOps chooses who to contact</span> (private or public) from the offer and ICP. You approve the list and the first email.
      </PageTip>

      <div className="flex items-center gap-1 mb-6 rounded-lg border border-border/60 bg-card/40 p-1 w-fit" data-testid="campaigns-tabs">
        <button onClick={() => setTab("campaigns")} className={`rounded-md px-3 py-1.5 text-sm ${tab === "campaigns" ? "bg-amber-500 text-black" : "text-muted-foreground hover:text-foreground"}`} data-testid="tab-campaigns">Campaigns</button>
        <button onClick={() => setTab("templates")} className={`rounded-md px-3 py-1.5 text-sm ${tab === "templates" ? "bg-amber-500 text-black" : "text-muted-foreground hover:text-foreground"}`} data-testid="tab-templates">Templates</button>
      </div>

      {tab === "templates" ? (
        <TemplatesPanel canManage={canManage} />
      ) : campaigns.length === 0 ? (
        <EmptyState icon={Send} title="No campaigns yet" body="Generate your first AI sequence from an offer." />
      ) : (
        <>
        {(() => {
          const agg = (pred) => campaigns.filter(pred).reduce((a, c) => ({ sent: a.sent + (c.analytics?.sent || 0), replied: a.replied + (c.analytics?.replied || 0), meetings: a.meetings + (c.analytics?.meetings || 0) }), { sent: 0, replied: 0, meetings: 0 });
          const priv = agg((c) => c.type !== "public_sector");
          const pub = agg((c) => c.type === "public_sector");
          const Row = ({ label, tone, d }) => (
            <div className="rounded-xl border border-border/60 bg-card/40 p-4" data-testid={`bytype-${label.toLowerCase().replace(/\s/g, "-")}`}>
              <div className={`text-xs font-medium mb-2 ${tone}`}>{label}</div>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Sent" value={d.sent} />
                <Metric label="Replied" value={d.replied} />
                <Metric label="Meetings" value={d.meetings} />
              </div>
            </div>
          );
          return (
            <div className="grid sm:grid-cols-2 gap-4 mb-6" data-testid="analytics-by-type">
              <Row label="Private sector" tone="text-amber-500" d={priv} />
              <Row label="Public sector" tone="text-sky-500" d={pub} />
            </div>
          );
        })()}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((c) => (
            <div key={c.id} role="button" tabIndex={0} onClick={() => navigate(`/app/campaigns/${c.id}`)}
              onKeyDown={(e) => { if (e.key === "Enter") navigate(`/app/campaigns/${c.id}`); }} data-testid={`campaign-card-${c.id}`}
              className="cursor-pointer text-left rounded-xl border border-border/70 bg-card/50 p-5 hover:border-amber-500/50 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <Badge variant="outline" className="capitalize text-xs">{c.status}</Badge>
                {c.type === "public_sector" && <span className="text-[10px] font-mono uppercase text-sky-500">gov</span>}
              </div>
              <div className="font-display font-semibold tracking-tight">{c.name}</div>
              <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{c.angle}</p>
              <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                <span>{(c.steps || []).length} steps</span>
                <span>{c.enrolled || 0} enrolled</span>
                <span className="text-amber-500">{c.auto_send ? "auto-send" : "approval on"}</span>
              </div>
              {c.analytics && (
                <div className="mt-3 grid grid-cols-3 gap-2" data-testid={`campaign-analytics-${c.id}`}>
                  <Metric label="Sent" value={c.analytics.sent} />
                  <Metric label="Replied" value={c.analytics.replied} sub={`${c.analytics.reply_rate}%`} />
                  <Metric label="Meetings" value={c.analytics.meetings} sub={`${c.analytics.meeting_rate}%`} />
                </div>
              )}
              <div className="mt-4 pt-3 border-t border-border/40 flex justify-end">
                <Button size="sm" variant="ghost" className="rounded-md text-xs"
                  onClick={(e) => { e.stopPropagation(); setDup(c); setDupOffer(c.offer_id || ""); }}
                  data-testid={`duplicate-btn-${c.id}`}><Copy className="h-3.5 w-3.5 mr-1.5" /> Duplicate</Button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      <Dialog open={!!dup} onOpenChange={(o) => { if (!o) setDup(null); }}>
        <DialogContent data-testid="duplicate-dialog">
          <DialogHeader>
            <DialogTitle>Duplicate campaign</DialogTitle>
            <DialogDescription>Creates a new draft with the same steps and tone. Nobody is enrolled and the first-touch goes back to Needs approval.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Offer for the copy</Label>
            <Select value={dupOffer} onValueChange={setDupOffer}>
              <SelectTrigger className="mt-1.5" data-testid="duplicate-offer-select"><SelectValue placeholder="Select an offer" /></SelectTrigger>
              <SelectContent>{offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.product}{o.id === dup?.offer_id ? " · same offer" : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter><Button onClick={duplicate} disabled={!dupOffer} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="duplicate-confirm-btn">Duplicate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PageBody>
  );
}
